import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TreeNode } from './HelperTree'
import collapseIcon from '../../assets/images/collapse-alt-svgrepo-com-w.svg'
import expandIcon from '../../assets/images/expand-svgrepo-com-w.svg'

export interface TreeSelectValue {
  id: number | string | null
  label: string
}

interface TreeSelectOption {
  id: number | string
  originalId: number | null
  label: string
  code: string
  children: TreeSelectOption[]
  [key: string]: any
}

interface Props {
  budgetTree: TreeNode[]
  value: TreeSelectValue | null
  onChange: (value: TreeSelectValue) => void
  placeholder?: string
}

// ── Convert TreeNode[] → select option tree with guaranteed unique IDs ──
function toOptions(nodes: TreeNode[]): TreeSelectOption[] {
  return nodes.map((node, index) => ({
    id: node.id ?? `node-${index}`,
    originalId: node.id ?? null,
    label: `${node.account} ${node.accountName}`,
    code: node.reportCode,
    children: toOptions(node.children || []),
  }))
}

// Helper to collect all node IDs that have children (expandable)
function getAllExpandableIds(nodes: TreeSelectOption[]): Set<number | string> {
  const ids = new Set<number | string>()
  const collect = (list: TreeSelectOption[]) => {
    list.forEach((node) => {
      if (node.children && node.children.length > 0) {
        ids.add(node.id)
        collect(node.children)
      }
    })
  }
  collect(nodes)
  return ids
}

const LABEL_STYLES: Record<string, React.CSSProperties> = {
  // Top-level Group Header: Boldest, dark navy, capitalized
  GH: {
    fontWeight: 600,
    fontSize: 13,
    color: '#0f172a', // Slate 900
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  // Section Header: Semi-bold, dark slate blue
  SH: {
    fontWeight: 600,
    fontSize: 13,
    color: '#1e293b', // Slate 800
  },
  // Sub-Section Header: Medium weight, slightly muted dark grey
  SSH: {
    fontWeight: 600,
    fontSize: 13,
    color: '#334155', // Slate 700
  },
  // Leaf Items (VP, VP/IP, IP): Regular weight, clean text color
  VP: {
    fontWeight: 300,
    fontSize: 12,
    color: 'var(--text-primary, #475569)', // Slate 600 fallback
  },
  'VP/IP': {
    fontWeight: 300,
    fontSize: 12,
    color: 'var(--text-primary, #475569)',
  },
  IP: {
    fontWeight: 300,
    fontSize: 12,
    color: 'var(--text-primary, #475569)',
  },
}

const INDENT = 10

// ── Recursive Row Component ──────────────────────────────────────
const TreeOptionRow: React.FC<{
  option: TreeSelectOption
  depth: number
  selectedId: number | string | null
  expandedIds: Set<number | string>
  query: string
  onSelect: (id: number | null, label: string) => void
  onToggle: (id: number | string) => void
}> = ({
  option,
  depth,
  selectedId,
  expandedIds,
  query,
  onSelect,
  onToggle,
}) => {
  const hasChildren = option.children.length > 0
  const isExpanded = expandedIds.has(option.id)
  const isSelected = selectedId === option.originalId
  const q = query.trim().toLowerCase()

  // Match current node or any descendant recursively
  const matchesSearch = useCallback(
    (node: TreeSelectOption): boolean => {
      if (!q) return true
      if (node.label.toLowerCase().includes(q)) return true
      return node.children.some(matchesSearch)
    },
    [q]
  )

  if (!matchesSearch(option)) return null

  const forceOpen = Boolean(q && option.children.some(matchesSearch))
  const showOpen = forceOpen || isExpanded

  return (
    <>
      <div
        className={`ts-row${isSelected ? ' ts-row--active' : ''}`}
        style={{
          paddingLeft: depth * INDENT + 2,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
        }}
        onClick={() => onSelect(option.originalId, option.label)}
      >
        {/* Expand toggle */}
        {hasChildren ? (
          <button
            type="button"
            className="ts-toggle"
            onClick={(e) => {
              e.stopPropagation()
              onToggle(option.id)
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: '0 4px',
              cursor: 'pointer',
            }}
            aria-label={showOpen ? 'Collapse' : 'Expand'}
          >
            <i
              className={
                showOpen
                  ? 'fa fa-minus-circle icn-yellow m-2'
                  : 'fa fa-plus-circle icn-black m-2'
              }
              aria-hidden="true"
            />
          </button>
        ) : (
          <span
            style={{
              display: 'inline-block',
              width: '5px',
              height: '5px',
              flexShrink: 0,
              background: '#000000',
              borderRadius: '50%',
            }}
          />
        )}

        {/* Label */}
        <span
          className="ts-label"
          title={option.label}
          style={
            isSelected
              ? { fontWeight: 600 }
              : LABEL_STYLES[option.code] ?? LABEL_STYLES.VP
          }
        >
          {option.label}
        </span>

        {/* Selected Checkmark */}
        {isSelected && (
          <i
            className="fa fa-check ts-check ms-auto text-primary"
            aria-hidden="true"
          />
        )}
      </div>

      {/* Render Subtree */}
      {hasChildren && showOpen && (
        <div>
          {option.children.map((child) => (
            <TreeOptionRow
              key={child.id}
              option={child}
              depth={depth + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              query={query}
              onSelect={onSelect}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </>
  )
}

// ── Main Custom Tree Select Component ─────────────────────────────
const CustomTreeSelect: React.FC<Props> = ({
  budgetTree,
  value,
  onChange,
  placeholder = 'Select Account Type',
}) => {
  const { t } = useTranslation('scenes.Budget.index')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<number | string>>(
    new Set()
  )
  const wrapRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const options = useMemo(() => toOptions(budgetTree), [budgetTree])

  const allExpandableIds = useMemo(
    () => getAllExpandableIds(options),
    [options]
  )

  const isAllExpanded = useMemo(() => {
    if (allExpandableIds.size === 0) return false
    return Array.from(allExpandableIds).every((id) => expandedIds.has(id))
  }, [allExpandableIds, expandedIds])

  const toggleExpandAll = useCallback(() => {
    if (isAllExpanded) {
      setExpandedIds(new Set())
    } else {
      setExpandedIds(new Set(allExpandableIds))
    }
  }, [isAllExpanded, allExpandableIds])

  // Automatically expand top 2 levels when options change
  useEffect(() => {
    const ids = new Set<number | string>()
    options.forEach((gh) => {
      ids.add(gh.id)
      gh.children.forEach((sh) => ids.add(sh.id))
    })
    setExpandedIds(ids)
  }, [options])

  // Click outside listener
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Auto-focus search input when opened
  useEffect(() => {
    if (open) {
      setQuery('')
      setTimeout(() => searchRef.current?.focus(), 50)
    }
  }, [open])

  const handleToggle = useCallback((id: number | string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  const handleSelect = useCallback(
    (id: number | null, label: string) => {
      onChange({ id, label })
      setOpen(false)
    },
    [onChange]
  )

  const q = query.trim().toLowerCase()

  // Helper function to check if a node or any of its descendants match the search query
  const matchesSearch = useCallback(
    (node: TreeSelectOption): boolean => {
      if (!q) return true
      if (node.label.toLowerCase().includes(q)) return true
      return node.children ? node.children.some(matchesSearch) : false
    },
    [q]
  )

  // Determine if any options match the current search query
  const hasMatches = useMemo(() => {
    if (options.length === 0) return false
    if (!q) return true
    return options.some(matchesSearch)
  }, [options, q, matchesSearch])

  const displayLabel = value?.label || placeholder
  const isAllSelected = value === null || value?.id === null || value?.id === ''

  return (
    <div ref={wrapRef} className="ts-wrap position-relative">
      {/* Trigger Button */}
      <button
        type="button"
        className={`ts-trigger form-select d-flex align-items-center justify-content-between${
          open ? ' ts-trigger--open' : ''
        }`}
        onClick={() => setOpen((o) => !o)}
        style={{
          height: '38px',
          width: '280px',
          fontSize: '13px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #d4d4d4',
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-truncate me-2">
          <i className="fa fa-filter me-1 text-muted" aria-hidden="true" />
          {displayLabel}
        </span>
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div
          className="ts-panel position-absolute bg-white border rounded shadow mt-1 p-2"
          style={{
            width: '280px',
            zIndex: 1050,
            maxHeight: '350px',
            overflowY: 'auto',
          }}
          role="listbox"
        >
          {/* Header Controls: Search Box & Collapse/Expand All Toggle */}
          <div className="d-flex align-items-center gap-2 mb-2">
            <div className="ts-search flex-grow-1 position-relative">
              <input
                ref={searchRef}
                type="text"
                className="form-control form-control-sm pe-4"
                placeholder={t('label.searchAccounts', 'Search accounts...')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button
                  type="button"
                  className="btn btn-sm btn-link position-absolute end-0 top-1 text-muted text-decoration-none"
                  onClick={() => setQuery('')}
                >
                  <i className="fa fa-x" aria-hidden="true" />
                </button>
              )}
            </div>

            <button
              type="button"
              className="btn btn-sm btn-primary border text-secondary d-flex align-items-center gap-1 px-1"
              style={{ fontSize: '11px', height: '25px', whiteSpace: 'nowrap' }}
              title={
                isAllExpanded
                  ? t('label.collapseAll', 'Collapse All')
                  : t('label.expandAll', 'Expand All')
              }
              onClick={toggleExpandAll}
            >
              <img
                src={isAllExpanded ? collapseIcon : expandIcon}
                alt=""
                style={{ height: '20px' }}
              />
            </button>
          </div>

          {/* Tree Menu */}
          <div className="ts-tree">
            {/* "All Accounts" Default Option */}
            <div
              className={`ts-all-row py-1 px-2 rounded cursor-pointer d-flex align-items-center ${
                isAllSelected ? 'bg-light font-weight-bold text-primary' : ''
              }`}
              onClick={() => handleSelect(null, t('label.all', 'All'))}
            >
              <i
                className="fa-solid fa-border-all me-2 text-muted"
                aria-hidden="true"
              ></i>
              <span className="ts-label">{t('label.all', 'All')}</span>
              {isAllSelected && (
                <i className="fa fa-check ms-auto" aria-hidden="true" />
              )}
            </div>

            <hr className="my-1" />

            {/* Rendered Nodes */}
            {!hasMatches ? (
              <div className="text-muted text-center py-2 fs-7">
                {t('label.noAccountsAvailable', 'No accounts available')}
              </div>
            ) : (
              options.map((option) => (
                <TreeOptionRow
                  key={option.id}
                  option={option}
                  depth={0}
                  selectedId={value?.id ?? null}
                  expandedIds={expandedIds}
                  query={query}
                  onSelect={handleSelect}
                  onToggle={handleToggle}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomTreeSelect
